import React, { useState } from 'react';

import Typography from '../../../../library/typography/typography';
import ProductArticleStatusBanner from '../../../../components/product-article-status-banner/product-article-status-banner.one-dt';
import ProductAvailability from '../../../../components/product-availability/product-availability';
import ProductDescription from '../../../../components/product-description/product-description';
import ProductImage from '../../../../components/product-image/product-image';
import ProductRating from '../../../../components/product-rating/product-rating';
import ProductSecondaryFlags from '../../../../components/product-secondary-flags/product-secondary-flags';
import ProductSwatchSelection from '../../../../components/product-swatch-selection/product-swatch-selection.one-dt';
import TireAttributeIcons from '../../../../components/tire-attribute-icons/tire-attribute-icons.one-dt';
import ViewOnVehicleButton from '../../../../components/view-on-vehicle-button/view-on-vehicle-button';
import WebOnlyFlag from '../../../../components/web-only-flag/web-only-flag';
import WinterTireBadge from '../../../../components/winter-tire-badge/winter-tire-badge';
import CompareCheckbox from '../../components/compare-checkbox/compare-checkbox';
import { WhiteAngleRegularIcon } from '../../../../shared/icons/icons';
import ProductListCardHighlight from '../product-list-card-highlight/product-list-card-highlight';

import {
  useOptimisticData,
  useProductEntryDataLayerValues,
  useProductSwatchSelection,
  useTreadwellPreferences
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import { getShouldShowCompare } from '../../../../shared/utils/block-utils/block-utils';
import {
  getAverageReviewRating,
  getNormalizedProductType,
  getNumberOfProductReviews,
  getPrioritizedArticleStatusProduct,
  getShouldShowProductArticleStatusBanner,
  getTireAttributeIcons,
  isCanonicalProduct,
  isWebTire,
  isWindShieldWiperProduct,
  isWinterTire
} from '../../../../shared/utils/product/product';

import { ProductData } from '../../../../shared/types/graphql-types';
import {
  PRODUCT_TYPES,
  TIRE_ATTRIBUTES
} from '../../../../shared/constants/products';

import './product-list-card-details.one-dt.scss';

interface ProductListCardDetailsProps {
  header?: string;
  hideAvailability?: boolean;
  hideCompare?: boolean;
  hideImage?: boolean;
  hideProductSize?: boolean;
  hideReviews?: boolean;
  hideViewOnVehicleAddToCart?: boolean;
  hideViewOnVehicleFilters?: boolean;
  onCheckInventoryClick: () => void;
  onViewProductClick: () => void;
  product?: ProductData;
  productDetailUrl: string;
  productSet?: Array<ProductData>;
  quantity: number;
  rearQuantity?: number;
}

function ProductListCardDetails(props: ProductListCardDetailsProps) {
  const {
    header = '',
    hideAvailability = false,
    hideCompare = false,
    hideImage = false,
    hideProductSize = false,
    hideReviews = false,
    hideViewOnVehicleAddToCart = false,
    hideViewOnVehicleFilters = false,
    onCheckInventoryClick,
    onViewProductClick,
    product,
    productDetailUrl,
    productSet,
    quantity,
    rearQuantity
  } = props;

  const {
    articleStatus,
    brand,
    code,
    gbb,
    name,
    rearProduct,
    swatches,
    vendorFinish
  } = product || {};

  const { isTreadwellFlow, treadwellPreferences } = useTreadwellPreferences();
  const optimisticData = useOptimisticData();
  const isCanonical = isCanonicalProduct(product);
  const isWinterOnly = isWinterTire(product);
  const isWebOnly = isWebTire(product);

  const isStaggered = Boolean(rearProduct);
  const productSize = isWindShieldWiperProduct(product)
    ? `${product?.size}"`
    : product?.size;
  const size = isCanonicalProduct(product) ? undefined : productSize;
  const productType = getNormalizedProductType(product);
  const accessoryImgSize = 240;
  const tireOrWheelProductImgSize = 300;

  const numberOfProductReview = getNumberOfProductReviews(product);
  const averageReviewRating = getAverageReviewRating(product);
  const shouldShowCompare = getShouldShowCompare(articleStatus);

  const { saveSelectedSwatch, selectedSwatch } = useProductSwatchSelection(
    code,
    swatches
  );

  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();

  const [productCode, setProductCode] = useState(code);

  const articleStatusProduct = getPrioritizedArticleStatusProduct(
    product,
    product?.rearProduct
  );
  const shouldShowProductArticleStatusBanner =
    getShouldShowProductArticleStatusBanner(articleStatusProduct);

  let styleName = 'product-img';

  if (productType === PRODUCT_TYPES.WHEELS) {
    styleName = 'product-img-wheel';
  } else if (productType === PRODUCT_TYPES.TIRES) {
    styleName = 'product-img-tire';
  }

  const isWinterTreadwellFlow = Boolean(treadwellPreferences?.showWinterTires);

  function setOptimisticProductData() {
    optimisticData.write({ product });
    onViewProductClick?.();
  }

  function onSwatchChange(swatch) {
    saveSelectedSwatch(swatch);
    setProductCode(swatch?.productCode);
  }

  function handleReadReviewsClick() {
    trackAction(
      'product_read_reviews_click',
      getProductEntryDataLayerValues(product)
    );
  }

  return (
    <div styleName="container">
      {!hideImage && (
        <div styleName="img-col">
          <ProductListCardHighlight
            hideTreadwellRank={!isTreadwellFlow}
            hideWebOnly
            product={product}
          />
          {process.env.__FF_FLEET_ACCOUNT__ === 'true' && (
            <ProductSecondaryFlags
              hideWebOnly={!product?.isFleetPreferred}
              product={product}
            />
          )}
          <ProductImage
            height={
              productType === PRODUCT_TYPES.ACCESSORIES
                ? accessoryImgSize
                : tireOrWheelProductImgSize
            }
            imageFormatType={
              process.env.__FF_PRODUCT_IMAGE_FORMATS_AUTO_SYNC__ === 'true'
                ? 'PRODUCT_MEDIUM'
                : 'PRODUCT'
            }
            imageUrl={selectedSwatch?.linkUrl}
            linkUrl={productDetailUrl}
            onClick={setOptimisticProductData}
            product={product}
            styleName={styleName}
            width={
              productType === PRODUCT_TYPES.ACCESSORIES
                ? accessoryImgSize
                : tireOrWheelProductImgSize
            }
          />

          {isWebOnly && !product?.isFleetPreferred && (
            <WebOnlyFlag className="product-list-card-details__web-only-flag" />
          )}

          {isWinterTreadwellFlow && (
            <div styleName="winter-tire-badge">
              <WinterTireBadge isWinterOnly={isWinterOnly} />
            </div>
          )}
          <WhiteAngleRegularIcon styleName="icon" />
        </div>
      )}

      <div styleName="desc-col">
        {header && (
          <Typography
            className="product-list-card-details__header"
            styleName="header"
            tag="div"
          >
            {header}
          </Typography>
        )}
        <div
          className="product-list-card-details__product-description"
          styleName={
            isStaggered
              ? 'product-description-staggered'
              : 'product-description'
          }
        >
          <div styleName="product-description-firstline">
            <ProductDescription brand={brand} />
            {shouldShowCompare &&
              !hideCompare &&
              !isCanonical &&
              productType === PRODUCT_TYPES.TIRES && (
                <CompareCheckbox
                  hideSelectMoreText
                  product={product}
                  quantity={quantity}
                  rearQuantity={rearQuantity}
                />
              )}
          </div>

          <ProductDescription
            link={productDetailUrl}
            name={name}
            onClick={setOptimisticProductData}
            productType={productType}
            size={isStaggered || hideProductSize ? undefined : size}
          />
        </div>

        {!hideReviews && productType === PRODUCT_TYPES.TIRES && (
          <ProductRating
            avgReviewRating={averageReviewRating}
            hideRating
            link={`${productDetailUrl}#read-reviews`}
            linkText={`${numberOfProductReview} Reviews`}
            numberOfReviews={numberOfProductReview}
            onReadReviewClick={handleReadReviewsClick}
            productCode={product?.code}
            rearProductCode={rearProduct?.code}
            showCount={false}
          />
        )}

        {productType === PRODUCT_TYPES.TIRES && (
          <div styleName="gbb-and-badges">
            {gbb && (
              <>
                <ProductDescription gbb={gbb} showGBBTooltip />
                <ellipse styleName="ellipse" />
              </>
            )}
            <TireAttributeIcons
              icons={[
                ...getTireAttributeIcons(product),
                ...(Number(
                  product?.recommendationData?.stopping?.rollingResistanceRating
                ) >= 4
                  ? [TIRE_ATTRIBUTES.TREADWELL_FUEL_EFFICIENT]
                  : [])
              ]}
              link={`${productDetailUrl}#tire-attribute-icons`}
            />
          </div>
        )}

        {!hideAvailability && product && (
          <div styleName="product-availability">
            {shouldShowProductArticleStatusBanner ? (
              <ProductArticleStatusBanner product={articleStatusProduct} />
            ) : (
              <ProductAvailability
                onCheckInventoryClick={onCheckInventoryClick}
                product={product}
                productSet={productSet}
                quantity={quantity}
                rearProduct={product?.rearProduct}
                rearQuantity={rearQuantity}
              />
            )}
          </div>
        )}

        {productType === PRODUCT_TYPES.WHEELS && (
          <>
            {productCode && (
              <ProductSwatchSelection
                onSwatchChange={onSwatchChange}
                productCode={productCode}
                productDetailsUrl={productDetailUrl}
                productFinish={vendorFinish}
                swatches={swatches}
              />
            )}

            <div styleName="view-on-vehicle">
              {product?.isViewableOnVehicle && (
                <ViewOnVehicleButton
                  hideAddProduct={hideViewOnVehicleAddToCart}
                  hideFilters={hideViewOnVehicleFilters}
                  isCanonical={isCanonical}
                  onClick={() =>
                    trackAction(
                      'product_list_view_on_vehicle',
                      getProductEntryDataLayerValues(product)
                    )
                  }
                  product={{
                    ...product,
                    url: productDetailUrl || product?.url
                  }}
                  selectedSwatch={selectedSwatch}
                  shouldSkipQuery={hideViewOnVehicleFilters}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ProductListCardDetails;
